"use client";

import { useCallback, useState } from "react";

import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Feedback";
import { Toast } from "@/components/ui/Toast";
import { isEmail } from "@/lib/validation";

type Status = "idle" | "sending" | "done" | "error";

/** Footer "get news and offers" sign-up → /api/subscribe/. */
export function FooterSignup() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");
  const [toastOpen, setToastOpen] = useState(false);
  const closeToast = useCallback(() => setToastOpen(false), []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isEmail(email)) {
      setStatus("error");
      setMessage("Please enter a valid email address.");
      return;
    }

    setStatus("sending");
    try {
      const response = await fetch("/api/subscribe/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = (await response.json().catch(() => ({}))) as {
        message?: string;
      };
      if (!response.ok)
        throw new Error(data.message || "Please try again later.");
      setStatus("done");
      setMessage(
        `${email.trim()} will get our news and offers. Unsubscribe anytime from any email.`,
      );
      setToastOpen(true);
      setEmail("");
    } catch (error) {
      setStatus("error");
      setMessage(
        error instanceof Error ? error.message : "Please try again later.",
      );
    }
  };

  return (
    <>
      <form
        onSubmit={handleSubmit}
        noValidate
        className="flex max-w-sm flex-col gap-2"
        aria-label="Email sign-up"
      >
        <label
          htmlFor="footer-signup-email"
          className="text-sm font-semibold text-white"
        >
          Get news and offers
        </label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            id="footer-signup-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              if (status !== "sending") setStatus("idle");
            }}
            className="border-line-strong bg-ink/60 focus:border-brand min-w-0 flex-1 rounded-xl border px-3 py-2 text-sm text-white outline-none"
          />
          <Button type="submit" size="sm" disabled={status === "sending"}>
            {status === "sending" ? <Spinner /> : null}
            Subscribe
          </Button>
        </div>
        <p
          role={status === "error" ? "alert" : "status"}
          className={
            status === "error"
              ? "text-danger text-xs"
              : "text-content-subtle text-xs"
          }
        >
          {status === "error"
            ? message
            : "Unsubscribe anytime with the link in every email."}
        </p>
      </form>
      <Toast
        open={toastOpen}
        tone="success"
        title="You're subscribed to our newsletter!"
        message={message}
        durationMs={6000}
        onClose={closeToast}
      />
    </>
  );
}
